import React, { useState } from 'react';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown';
import { faMinusCircle } from '@fortawesome/free-solid-svg-icons/faMinusCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Accordion, { AccordionContent, AccordionTitle } from '../../Accordion/accordionComponents';
import ApplicationOptions from 'buyside/components/presentation/JobApplicationShowPage/ApplicationOptions/ApplicationOptions';
import Badges from 'buyside/components/presentation/Badges/badges';
import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import classnames from 'classnames';
import EmployerAssociation from 'buyside/components/presentation/EmployerAssociation/employerAssociation';
import Envelope from 'SVGs/envelope.svg';
import FollowButton, {
  FollowableType
} from 'buyside/components/presentation/FollowButton/followButton';
import JobApplicationShowMutations from 'buyside/components/container/JobApplicationShowPage/jobApplicationShowPageMutationsContainer';
import Link from 'buyside/components/presentation/Link/link';
import moment from 'moment';
import PDFIcon from 'buyside/svgs/document/file-pdf.svg';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';

import { defaultUserImageUrl, userUrl, newMessageUrl, userIdeaUrl } from 'buyside/util/urlHelper';
import { formatPhoneNumber, truncateText } from 'buyside/util/textHelpers';
const { jobApplicationStatuses } = require('buyside/util/constantsFromRails.json');

import { JobApplication as JobApplicationType, Maybe } from 'interfaces/graphql-typings';

import styles from './jobApplicationIndexCardStyles.scss';

export const CoverNote: React.FC<{ coverNote: string }> = ({ coverNote }) => {
  const [show, toggleShow] = useState(false);
  const truncateLength = 200;

  return (
    <div className={styles.coverNote}>
      <h5 className={styles.subheader}>COVER NOTE: </h5>

      <p>
        <span className={styles.coverNoteText}>
          {show && coverNote.length > truncateLength
            ? coverNote
            : truncateText(coverNote, truncateLength)}
        </span>

        {coverNote.length > truncateLength && (
          <span className={styles.coverNoteToggleShow}>
            <Button
              onClick={(): void => toggleShow(!show)}
              type={BUTTON_TYPES.LINK}
              text={`Show ${show ? 'Less' : 'More'}`}
            />
          </span>
        )}
      </p>
    </div>
  );
};

const JobApplicationIndexCard: React.FC<Pick<
  JobApplicationType,
  'createdAt' | 'coverNote' | 'email' | 'name' | 'phone' | 'resumeUrl' | 'status'
> & {
  applicantCity: Maybe<string>;
  applicantEmployerId: Maybe<string>;
  applicantEmployerName: Maybe<string>;
  applicantRecentEmployerTitle: Maybe<string>;
  hasBadges: boolean;
  isApplicantCurrentlyEmployed: boolean;
  jobApplicationId: string;
  jobPostingId: string;
  jobPostingTitle: string;
  profileImageUrl: Maybe<string>;
  profileSummary: Maybe<string>;
  publishedIdeasCount?: number;
  starred: boolean;
  userBadges: any;
  userId: string;
}> = ({
  applicantCity,
  applicantEmployerId,
  applicantEmployerName,
  applicantRecentEmployerTitle,
  coverNote,
  createdAt,
  email,
  hasBadges,
  isApplicantCurrentlyEmployed,
  jobApplicationId,
  jobPostingId,
  jobPostingTitle,
  name,
  phone,
  profileImageUrl,
  profileSummary,
  publishedIdeasCount,
  resumeUrl,
  starred,
  status,
  userBadges,
  userId
}) => {
  return (
    <Accordion
      initialOpen={
        status !== jobApplicationStatuses.rejected &&
        status !== jobApplicationStatuses.interviewed_and_rejected
      }
    >
      <AccordionTitle collapseIcon={faAngleUp} expandable expandIcon={faAngleDown}>
        <Link
          className={classnames(styles.applicantProfileLink, {
            [styles.rejectedApplicantName]: status === jobApplicationStatuses.rejected
          })}
          to={userUrl(userId)}
        >
          <ProfileImage size={IMAGE_SIZES.TINY} source={profileImageUrl || defaultUserImageUrl()} />
          <p>{name}</p>
        </Link>
      </AccordionTitle>

      <AccordionContent>
        <div className={styles.upper}>
          <div className={styles.profileDetails}>
            {isApplicantCurrentlyEmployed && (
              <EmployerAssociation
                style={null}
                mostRecentEmployer={{
                  currentEmployer: isApplicantCurrentlyEmployed,
                  title: applicantRecentEmployerTitle,
                  employer: { id: applicantEmployerId, name: applicantEmployerName }
                }}
              />
            )}

            {Boolean(applicantCity) && <div className={styles.applicantCity}>{applicantCity}</div>}
          </div>

          {(Boolean(publishedIdeasCount) || hasBadges) && (
            <div className={styles.applicantAchievements}>
              {Boolean(publishedIdeasCount) && (
                <div className={styles.publishedIdeasCount}>
                  <span className={styles.subheader}>IDEAS:</span>
                  <Link to={userIdeaUrl(userId)}>{publishedIdeasCount}</Link>
                </div>
              )}
              {hasBadges && (
                <div className={styles.badges}>
                  <Badges className={styles.subheader} userBadges={userBadges} />
                </div>
              )}
            </div>
          )}
        </div>

        {Boolean(profileSummary) && (
          <p className={styles.profileSummary}>{truncateText(profileSummary, 200)}</p>
        )}

        <div className={styles.optionButtons}>
          {resumeUrl && <Button icon={PDFIcon} url={resumeUrl} text="View Resume" />}
          <Button
            type={BUTTON_TYPES.SECONDARY}
            text="Message"
            icon={Envelope}
            url={newMessageUrl(userId)}
          />
          <FollowButton
            type={FollowableType.USER}
            buttonType={BUTTON_TYPES.SECONDARY}
            id={userId}
          />
        </div>

        <h5 className={styles.appliedOn}>
          <span className={styles.subheader}>APPLIED ON: </span>
          <p>{moment(createdAt).format('M/D/YY')}</p>
        </h5>

        {coverNote && <CoverNote coverNote={coverNote} />}

        {Boolean(phone) && (
          <>
            <span className={styles.subheader}>PHONE: </span>
            <p className={styles.applicantPhone}>{formatPhoneNumber(phone || '')}</p>
          </>
        )}

        {Boolean(email) && (
          <>
            <span className={styles.subheader}>EMAIL: </span>
            <Link className={styles.applicantEmail} to={`mailto:${email}`}>
              <span>{email}</span>
            </Link>
          </>
        )}

        <div className={styles.optionContainer}>
          <div className={styles.options}>
            {status !== jobApplicationStatuses.rejected &&
              status !== jobApplicationStatuses.interviewed_and_rejected && (
                <JobApplicationShowMutations
                  jobApplication={{
                    id: jobApplicationId,
                    userId,
                    name,
                    phone,
                    email,
                    coverNote,
                    otherApplications: [],
                    jobPosting: { id: jobPostingId, title: jobPostingTitle },
                    status,
                    resumeUrl,
                    starred
                  }}
                >
                  {({
                    handleInterviewed,
                    handleRejected,
                    handleStarred,
                    isFetching
                  }): React.ReactElement => (
                    <ApplicationOptions
                      status={status}
                      name={name || ''}
                      jobPostingId=""
                      otherApplicationsCount={0}
                      starred={Boolean(starred)}
                      handleInterviewed={handleInterviewed}
                      handleRejected={handleRejected}
                      handleStarred={handleStarred}
                      isFetching={isFetching}
                    />
                  )}
                </JobApplicationShowMutations>
              )}

            {status === jobApplicationStatuses.rejected && (
              <div className={styles.applicationRejected}>
                <FontAwesomeIcon icon={faMinusCircle} />
                <p>This application has been rejected</p>
              </div>
            )}
          </div>
        </div>
      </AccordionContent>
    </Accordion>
  );
};

export default JobApplicationIndexCard;
